import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Roulette({ cardInstanceId, card, onFinish, compact = false, autoSpin = false }) {
  const [values, setValues] = useState([5, 10, 20, 50]);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then(r => setValues(r.data.roulette_values || [5,10,20,50]));
  }, []);

  const autoSpinFired = useRef(false);
  useEffect(() => {
    if (autoSpin && !autoSpinFired.current) {
      autoSpinFired.current = true;
      spin();
    }
  }, [autoSpin]);

  const finishedRef = useRef(false);
  useEffect(() => {
    if (compact && result != null && !finishedRef.current) {
      finishedRef.current = true;
      onFinish(result);
    }
  }, [compact, result]);

  const slice = 360 / values.length;
  // Indicator at top (12 o'clock), wheel rotates clockwise
  const colors = ["#2E523A", "#D4AF37", "#9C413D", "#4A7C59", "#1C2920", "#7B5C2A"];

  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    try {
      const r = await api.post(`/cards/${cardInstanceId}/claim`);
      const value = r.data.value_eur;
      const idx = values.indexOf(value);
      // We want the slice at index idx to land at top.
      // Slice idx center angle in wheel-coord: idx*slice + slice/2 (clockwise from 12)
      // After rotating wheel by R, that center is at R + idx*slice + slice/2 (mod 360).
      // To land at top (0), R ≡ -(idx*slice + slice/2)  (mod 360)
      const target = -(idx * slice + slice / 2);
      const turns = 6;
      const newRotation = rotation - (rotation % 360) + turns * 360 + target;
      setRotation(newRotation);
      setTimeout(() => { setResult(value); setSpinning(false); }, 4200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na roleta");
      setSpinning(false);
    }
  };

  const wheel = (
    <div className={`relative mx-auto ${compact ? "w-28 h-28 md:w-32 md:h-32" : "w-72 h-72 md:w-80 md:h-80"}`}>
      {/* Pointer */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-10" style={{
        width: 0, height: 0,
        borderLeft: `${compact ? 7 : 14}px solid transparent`, borderRight: `${compact ? 7 : 14}px solid transparent`,
        borderTop: `${compact ? 11 : 22}px solid #D4AF37`, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))"
      }} />
      <motion.svg viewBox="-110 -110 220 220" className="roulette-wheel w-full h-full"
        animate={{ rotate: rotation }} transition={{ duration: 4, ease: [0.17, 0.67, 0.13, 0.99] }}>
        <circle r="105" fill="#0A100D" stroke="#D4AF37" strokeWidth="3" />
        {values.map((v, i) => {
          const a0 = (i * slice - 90) * Math.PI / 180;
          const a1 = ((i + 1) * slice - 90) * Math.PI / 180;
          const x0 = 100 * Math.cos(a0), y0 = 100 * Math.sin(a0);
          const x1 = 100 * Math.cos(a1), y1 = 100 * Math.sin(a1);
          const large = slice > 180 ? 1 : 0;
          const mid = (a0 + a1) / 2;
          const tx = 65 * Math.cos(mid), ty = 65 * Math.sin(mid);
          const color = colors[i % colors.length];
          return (
            <g key={i}>
              <path d={`M0,0 L${x0},${y0} A100,100 0 ${large} 1 ${x1},${y1} Z`} fill={color} stroke="#D4AF37" strokeWidth="1.5"/>
              <text x={tx} y={ty} fill="#F1E8D9" fontSize={compact ? 26 : 22} fontFamily="Cormorant Garamond, serif" fontWeight="700"
                textAnchor="middle" dominantBaseline="middle" transform={`rotate(${(i * slice + slice/2)} ${tx} ${ty})`}>{v}€</text>
            </g>
          );
        })}
        <circle r="14" fill="#D4AF37" stroke="#0A100D" strokeWidth="3"/>
      </motion.svg>
    </div>
  );

  if (compact) {
    return (
      <div className="glass p-4 text-center" data-testid={`mini-roulette-${cardInstanceId}`}>
        <p className="text-xs text-[color:var(--text-3)] mb-2 truncate">{card.name}</p>
        {wheel}
        <div className="mt-3">
          {result == null ? (
            <button onClick={spin} disabled={spinning} className="btn-primary !py-1.5 !px-4 text-sm" data-testid={`spin-roulette-button-${cardInstanceId}`}>
              {spinning ? "A girar…" : "Girar"}
            </button>
          ) : (
            <div className="font-display text-2xl text-gold">{result}€</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background: "rgba(10,16,13,0.86)", backdropFilter: "blur(12px)" }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="glass max-w-2xl w-full p-8 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2 flex items-center justify-center gap-2"><Sparkles size={14}/> Prémio revelado</p>
        <h2 className="font-display text-4xl mb-1">{card.name}</h2>
        <p className="text-[color:var(--text-2)] mb-8">Gira a roleta para descobrir o valor do teu voucher</p>

        {wheel}

        <div className="mt-8">
          {result == null ? (
            <button onClick={spin} disabled={spinning} className="btn-primary text-lg" data-testid="spin-roulette-button">
              {spinning ? "A girar…" : "Girar a Roleta"}
            </button>
          ) : (
            <div>
              <div className="font-display text-5xl text-gold mb-2">{result}€</div>
              <p className="text-[color:var(--text-2)] mb-6">Voucher adicionado ao teu saldo</p>
              <button onClick={() => onFinish(result)} className="btn-primary" data-testid="claim-voucher-button">Continuar</button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
